import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PlusCircle, X, Loader2, AlertCircle, MapPin, Phone, Mail, User, Trash2, Search } from 'lucide-react';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

const TrainingCentersContent = () => {
  const [trainingCenters, setTrainingCenters] = useState([]);
  const [filteredCenters, setFilteredCenters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    address: '',
    phone: '',
    email: '',
    contact_person: '',
  });

  // Listen for the triggerAdd event from the parent component
  useEffect(() => {
    const handleTriggerAdd = (event) => {
      if (event.detail.tab === 'centers') {
        setIsEditing(false);
        setShowModal(true);
      }
    };

    window.addEventListener('triggerAdd', handleTriggerAdd);
    return () => {
      window.removeEventListener('triggerAdd', handleTriggerAdd);
    };
  }, []);

  // Fetch data from API
  useEffect(() => {
    const fetchTrainingCenters = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/training-centers/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
        });
        setTrainingCenters(response.data);
        setFilteredCenters(response.data);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch training centers');
        setLoading(false);
        console.error(err);
      }
    };

    fetchTrainingCenters();
  }, []);

  // Filter centers based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredCenters(trainingCenters);
    } else {
      const filtered = trainingCenters.filter(center =>
        center.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        center.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
        center.phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
        center.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (center.contact_person && center.contact_person.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredCenters(filtered);
    }
  }, [searchTerm, trainingCenters]);

  // Email validation function
  const validateEmail = (email) => {
    // Django's EmailField regex pattern (simplified but compatible)
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Clear email validation error when user types
    if (name === 'email' && error && error.includes('email')) {
      setError(null);
    }
  };

  // Reset form and close modal
  const resetForm = () => {
    setFormData({ id: null, name: '', address: '', phone: '', email: '', contact_person: '' });
    setShowModal(false);
    setIsEditing(false);
    setShowDeleteConfirm(false);
  };

  // Add or update training center
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side email validation
    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address (e.g., user@example.com)');
      return;
    }
    
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      };

      if (isEditing) {
        const response = await axios.put(
          `${API_BASE_URL}/training-centers/${formData.id}/`,
          formData,
          config
        );
        setTrainingCenters((prev) =>
          prev.map((center) => (center.id === response.data.id ? response.data : center))
        );
      } else {
        const response = await axios.post(`${API_BASE_URL}/training-centers/`, formData, config);
        setTrainingCenters((prev) => [...prev, response.data]);
      }

      resetForm();
    } catch (err) {
      setError(`Failed to ${isEditing ? 'update' : 'add'} training center`);
      console.error(err);
    }
  };

  // Open delete confirmation modal
  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  // Confirm and delete training center
  const handleDeleteCenter = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/training-centers/${formData.id}/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      setTrainingCenters((prev) => prev.filter((center) => center.id !== formData.id));
      resetForm();
    } catch (err) {
      setError('Failed to delete training center');
      console.error(err);
    }
  };

  // Open modal in edit mode with selected center data
  const handleEditCenter = (center) => {
    setFormData({
      id: center.id,
      name: center.name,
      address: center.address,
      phone: center.phone,
      email: center.email,
      contact_person: center.contact_person || '',
    });
    setIsEditing(true);
    setShowModal(true);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-steel-50 rounded-xl shadow">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-brand-500 mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Loading training centers...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64 bg-red-50 rounded-xl shadow">
        <div className="text-center max-w-md p-6">
          <AlertCircle className="h-10 w-10 text-red-600 mx-auto mb-4" />
          <h3 className="text-red-800 font-bold text-lg mb-2">An error occurred</h3>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Search bar */}
      <div className="px-6 pt-4 pb-2">
        <div className="relative">
          <Search className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search training centers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full border border-steel-300 rounded-xl pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
          />
        </div>
      </div>

      {/* Table container */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="flex-1 overflow-y-auto">
          <table className="min-w-full divide-y divide-steel-200">
            <thead className="bg-steel-50 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                  Address
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                  Phone
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                  Contact Person
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-steel-200">
              {filteredCenters.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="max-w-sm mx-auto">
                      <div className="bg-steel-100 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <AlertCircle className="h-8 w-8 text-slate-400" />
                      </div>
                      <p className="text-slate-600 font-medium mb-2">
                        {searchTerm ? 'No training centers match your search' : 'No training centers found'}
                      </p>
                      <p className="text-slate-500 text-sm mb-4">
                        {searchTerm ? 'Try adjusting your search terms.' : 'Add your first training center to see it on the list.'}
                      </p>
                      {!searchTerm && (
                        <button
                          onClick={() => setShowModal(true)}
                          className="bg-brand-500 text-white px-4 py-2 rounded-md hover:bg-brand-600 transition-colors inline-flex items-center"
                        >
                          <PlusCircle className="h-4 w-4 mr-2" />
                          Add Now
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredCenters.map((center) => (
                  <tr
                    key={center.id}
                    className="hover:bg-brand-50 transition-colors cursor-pointer"
                    onDoubleClick={() => handleEditCenter(center)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-slate-900">{center.name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-slate-700 flex items-start">
                        <MapPin className="h-4 w-4 text-slate-400 mr-1 mt-0.5 flex-shrink-0" />
                        <span>{center.address}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-brand-100 text-brand-700">
                          <Phone className="h-3 w-3 mr-1" />
                          {center.phone}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <a 
                          href={`mailto:${center.email}`}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 hover:bg-green-200 transition-colors"
                        >
                          <Mail className="h-3 w-3 mr-1" />
                          {center.email}
                        </a>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        {center.contact_person ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-steel-100 text-slate-800">
                            <User className="h-3 w-3 mr-1" />
                            {center.contact_person}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <User className="h-3 w-3 mr-1" />
                            Not specified
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleEditCenter(center)}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-brand-500 bg-brand-50 rounded-md hover:bg-brand-100 transition-colors duration-200"
                      >
                        Edit Center
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal for adding or editing a training center */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-0 overflow-hidden">
            <div className="bg-brand-500 text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold">
                {isEditing ? 'Edit Training Center' : 'Add New Training Center'}
              </h3>
              <button
                onClick={resetForm}
                className="text-white bg-brand-400 hover:bg-brand-600 rounded-full p-1 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="block w-full border border-steel-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                    placeholder="Enter center name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="block w-full border border-steel-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                    rows="3"
                    placeholder="Enter full center address"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                    <div className="relative">
                      <Phone className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                      <input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="block w-full border border-steel-300 rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                        placeholder="+1 123 456 789"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                    <div className="relative">
                      <Mail className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`block w-full border rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                          formData.email && !validateEmail(formData.email)
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-steel-300 focus:ring-brand-500'
                        }`}
                        placeholder="email@example.com"
                        pattern="[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
                        title="Please enter a valid email address"
                        required
                      />
                      {formData.email && !validateEmail(formData.email) && (
                        <p className="mt-1 text-sm text-red-600">
                          Please enter a valid email address
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Contact Person</label>
                  <div className="relative">
                    <User className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    <input
                      type="text"
                      name="contact_person"
                      value={formData.contact_person}
                      onChange={handleInputChange}
                      className="block w-full border border-steel-300 rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                      placeholder="Contact person's name and surname"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center mt-8 border-t pt-6">
                {isEditing && (
                  <button
                    type="button"
                    onClick={handleDeleteClick}
                    className="bg-red-600 text-white px-5 py-2 rounded-xl hover:bg-red-700 transition-colors font-medium flex items-center shadow-sm"
                  >
                    <Trash2 className="h-5 w-5 mr-2" />
                    Delete Center
                  </button>
                )}
                <div className="flex space-x-4 ml-auto">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="bg-white text-slate-700 border border-steel-300 px-5 py-2 rounded-xl hover:bg-steel-50 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-brand-500 text-white px-5 py-2 rounded-xl hover:bg-brand-600 transition-colors font-medium shadow-sm"
                  >
                    {isEditing ? 'Save Changes' : 'Add Center'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-center mb-4">
              <AlertCircle className="h-12 w-12 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 text-center mb-2">
              Confirm Deletion
            </h3>
            <p className="text-slate-600 text-center mb-6">
              Are you sure you want to delete the center <span className="font-semibold">"{formData.name}"</span>? 
              This action cannot be undone.
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="bg-white text-slate-700 border border-steel-300 px-5 py-2 rounded-xl hover:bg-steel-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCenter}
                className="bg-red-600 text-white px-5 py-2 rounded-xl hover:bg-red-700 transition-colors font-medium flex items-center"
              >
                <Trash2 className="h-5 w-5 mr-2" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingCentersContent;